import { Button, ContentLoader } from '@rio-cloud/rio-uikit';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { OCCURRENCE_HISTORY_PAGE_SIZE, useChassisOccurrenceHistory } from '../../../hooks/useChassisOccurrenceHistory';
import { formatDateTimeWithTimezone } from '../../../utils/dateUtils';
import { OPEN_TAS_OCCURRENCE_STATUSES } from '../../../api/tas';
import { getSupportSubtopicIntlLabel } from '../getSupportIntlLabels';

const TITLE_ID = 'history-occurrence-title';
const MIN_HEIGHT = 220;
const SKELETON_KEYS = Array.from({ length: OCCURRENCE_HISTORY_PAGE_SIZE }, (_, index) => `skeleton-${index}`);

interface Props {
    chassis?: string;
    currentOccurrenceId?: string | null;
}

const HistoryOccurrenceCardSkeleton = () => (
    <li className='border rounded padding-15'>
        <div className='display-flex justify-content-between align-items-center'>
            <ContentLoader width={140} height={20} />
            <ContentLoader width={18} height={18} />
        </div>
        <div className='display-flex justify-content-between align-items-center margin-top-10'>
            <ContentLoader width={70} height={16} />
            <ContentLoader width={120} height={16} />
        </div>
    </li>
);

const HistoryOccurrence = ({ chassis, currentOccurrenceId }: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const { occurrences, hasMore, isLoading, isLoadingMore, loadMore } = useChassisOccurrenceHistory(
        chassis,
        currentOccurrenceId
    );
    const listRef = useRef<HTMLUListElement>(null);
    const pendingScrollIndexRef = useRef<number | null>(null);
    const autoLoadedAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (isLoading) {
            autoLoadedAtRef.current = null;
        }

        const list = listRef.current;
        if (!list || isLoading || isLoadingMore || !hasMore) {
            return;
        }

        const fillFreeSpace = () => {
            const hasFreeSpace = list.clientHeight > 0 && list.scrollHeight <= list.clientHeight;

            if (hasFreeSpace && autoLoadedAtRef.current !== occurrences.length) {
                autoLoadedAtRef.current = occurrences.length;
                loadMore();
            }
        };

        fillFreeSpace();
        const observer = new ResizeObserver(fillFreeSpace);
        observer.observe(list);

        return () => observer.disconnect();
    }, [occurrences.length, isLoading, isLoadingMore, hasMore, loadMore]);

    useEffect(() => {
        const pendingIndex = pendingScrollIndexRef.current;
        if (pendingIndex === null || isLoadingMore) {
            return;
        }

        const firstNewCard = listRef.current?.children[pendingIndex] as HTMLElement | undefined;
        if (!isLoading && firstNewCard) {
            listRef.current?.scrollTo({ top: firstNewCard.offsetTop, behavior: 'smooth' });
        }
        pendingScrollIndexRef.current = null;
    }, [occurrences, isLoading, isLoadingMore]);

    const handleOccurrenceClick = (occurrenceId: number) => {
        const params = new URLSearchParams(location.search);
        params.set('id', String(occurrenceId));
        params.delete('tab');

        navigate(`${location.pathname}?${params.toString()}`);
    };

    const handleLoadMore = () => {
        pendingScrollIndexRef.current = occurrences.length;
        loadMore();
    };

    const formatHistoryValue = (group: 'status' | 'subject', value?: string) => {
        const id = `intl-msg:tas.support.history.${group}.${value}`;
        return value && intl.messages[id] ? intl.formatMessage({ id }) : '-';
    };

    const dateTimeLabel = intl.formatMessage({ id: 'intl-msg:tas.support.history.dateTime' });
    const subjectLabel = intl.formatMessage({ id: 'intl-msg:tas.support.history.subjectLabel' });
    const subtopicLabel = intl.formatMessage({ id: 'intl-msg:tas.support.history.subtopicLabel' });
    const statusLabel = intl.formatMessage({ id: 'intl-msg:tas.support.history.statusLabel' });
    const isEmpty = !isLoading && occurrences.length === 0 && !hasMore;

    const renderCards = () => {
        if (isLoading) {
            return SKELETON_KEYS.map(key => <HistoryOccurrenceCardSkeleton key={key} />);
        }

        return occurrences.map(occurrence => {
            const occurrenceNumber = occurrence.tasOccurrenceNumber || '-';
            const openLabel = intl.formatMessage(
                { id: 'intl-msg:tas.support.history.openOccurrence' },
                { number: occurrenceNumber }
            );

            return (
                <li key={occurrence.id} className='border rounded padding-15' data-testid='history-occurrence-card'>
                    <div className='display-flex flex-nowrap justify-content-between align-items-center gap-10'>
                        <span className='text-size-16 text-medium text-color-darkest'>{occurrenceNumber}</span>
                        <Button
                            bsStyle={Button.PRIMARY}
                            variant={Button.VARIANT_LINK}
                            bsSize={Button.XS}
                            iconOnly
                            noRippleEffect
                            title={openLabel}
                            aria-label={openLabel}
                            onClick={() => handleOccurrenceClick(occurrence.id)}
                        >
                            <span className='rioglyph rioglyph-new-window text-size-18' aria-hidden='true' />
                        </Button>
                    </div>
                    <div className='display-flex flex-nowrap justify-content-between align-items-center gap-10 margin-top-10 text-size-14'>
                        <span className='text-color-dark'>{dateTimeLabel}</span>
                        <span className='text-color-darker'>
                            {occurrence.createdAt ? formatDateTimeWithTimezone(occurrence.createdAt) : '-'}
                        </span>
                    </div>
                    <div className='display-flex flex-nowrap justify-content-between align-items-center gap-10 margin-top-10 text-size-14'>
                        <span className='text-color-dark'>{subjectLabel}</span>
                        <span className='text-color-darker'>{formatHistoryValue('subject', occurrence.subject)}</span>
                    </div>
                    <div className='display-flex flex-nowrap justify-content-between align-items-center gap-10 margin-top-10 text-size-14'>
                        <span className='text-color-dark'>{subtopicLabel}</span>
                        <span className='text-color-darker'>
                            {getSupportSubtopicIntlLabel(intl, occurrence.subtopic)}
                        </span>
                    </div>
                    <div className='display-flex flex-nowrap justify-content-between align-items-center gap-10 margin-top-10 text-size-14'>
                        <span className='text-color-dark'>{statusLabel}</span>
                        <span className='text-color-darker'>{formatHistoryValue('status', occurrence.status)}</span>
                    </div>
                </li>
            );
        });
    };

    return (
        <div className='position-relative flex-1-1 margin-top-10 width-100pct' style={{ minHeight: MIN_HEIGHT }}>
            <div className='position-absolute inset-0 display-flex flex-column flex-nowrap border rounded bg-white padding-15'>
                <div className='flex-none margin-bottom-15'>
                    <span id={TITLE_ID} className='text-size-16 text-medium text-color-darker'>
                        {intl.formatMessage({ id: 'intl-msg:tas.support.history.title' })}
                    </span>
                </div>

                {isEmpty ? (
                    <p className='text-center text-color-gray text-size-14 padding-y-20 margin-0'>
                        {intl.formatMessage({ id: 'intl-msg:tas.support.history.empty' })}
                    </p>
                ) : (
                    <ul
                        ref={listRef}
                        className='list-unstyled display-flex flex-column flex-nowrap gap-10 margin-0 padding-right-5 position-relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden'
                        aria-labelledby={TITLE_ID}
                        aria-busy={isLoading || isLoadingMore}
                    >
                        {renderCards()}
                    </ul>
                )}

                {!isLoading && hasMore ? (
                    <div className='flex-none display-flex justify-content-center margin-top-10'>
                        <Button
                            bsStyle={Button.PRIMARY}
                            variant={Button.VARIANT_LINK}
                            iconName={isLoadingMore ? 'rioglyph-spinner spinning' : 'rioglyph-arrow-down'}
                            disabled={isLoadingMore}
                            onClick={handleLoadMore}
                        >
                            {intl.formatMessage({ id: 'intl-msg:tas.support.history.loadMore' })}
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default HistoryOccurrence;
